import { useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/system";
import AddIcon from "@mui/icons-material/Add";
import { settings } from "app";
import BackButtonToolbar from "features/BackButtonToolbar";
import {
  Labeled,
  ReferenceField,
  Show,
  SimpleShowLayout,
  TextField,
  UrlField,
  useRecordContext,
} from "react-admin";
import { administrationRequests } from "api/administrationApi";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import SeatsList from "./SeatsList";
import AddSeatForm from "./AddSeatForm";
import ExamsSection from "./ExamsSection";
import LanguageExamsSection from "./LanguageExamsSection";
import DocumentTypesList from "./DocumentTypesList";
import AddDocumentTypeForm from "./AddDocumentTypeForm";
import EditDocumentTypeForm from "./EditDocumentTypeForm";
import RankingsSection from "./RankingsSection";
import DoctoralDateField from "../../Partials/DoctoralDateField";
import { SectionTitle } from "features/SectionTitle";

const SeatsSection = () => {
  const campaign = useRecordContext();
  const [addMode, setAddMode] = useState(false);

  if (!campaign) return null;

  return (
    <>
      <SecuredComponent permission={permissions.canUpdateDoctoralCampaigns}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddMode(true)}
            disabled={addMode}
          >
            Добави място
          </Button>
        </Box>
      </SecuredComponent>
      <SeatsList />
      {addMode && (
        <Box sx={{ mt: 2 }}>
          <AddSeatForm campaign={campaign} onDone={() => setAddMode(false)} />
        </Box>
      )}
    </>
  );
};

const DocumentTypesSection = () => {
  const campaign = useRecordContext();
  const [addMode, setAddMode] = useState(false);
  const [editingDocumentType, setEditingDocumentType] = useState(null);

  if (!campaign) return null;

  const isActive = !!campaign.dateFrom && new Date(campaign.dateFrom) <= new Date();

  return (
    <>
      {isActive ? (
        <Typography
          variant="caption"
          sx={{ display: "block", textAlign: "right", mb: 1, color: "text.secondary" }}
        >
          Кампанията е активна — типовете документи са заключени.
        </Typography>
      ) : (
        <SecuredComponent permission={permissions.canManageDoctoralCampaignDocumentTypes}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingDocumentType(null);
                setAddMode(true);
              }}
              disabled={addMode || editingDocumentType !== null}
            >
              Добави документ
            </Button>
          </Box>
        </SecuredComponent>
      )}
      <DocumentTypesList
        isActive={isActive}
        onEditDocumentType={documentType => {
          setAddMode(false);
          setEditingDocumentType(documentType);
        }}
      />
      {addMode && (
        <Box sx={{ mt: 2 }}>
          <AddDocumentTypeForm campaign={campaign} onDone={() => setAddMode(false)} />
        </Box>
      )}
      {editingDocumentType && (
        <Box sx={{ mt: 2 }}>
          <EditDocumentTypeForm
            campaign={campaign}
            documentType={editingDocumentType}
            onDone={() => setEditingDocumentType(null)}
          />
        </Box>
      )}
    </>
  );
};

const ShowDoctoralCampaign = () => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));

  return (
    <Show
      title="Детайли за докторантска кампания"
      actions={<BackButtonToolbar />}
    >
      <SimpleShowLayout>
        <SectionTitle
          title="Основни данни"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Grid
          container
          spacing={3}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid item xs={12} md={6}>
            <Labeled label="Име на български език">
              <TextField source="nameBg" />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={6}>
            <Labeled label="Име на английски език">
              <TextField source="nameEn" />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={4}>
            <Labeled label="Факултет">
              <ReferenceField
                source="structuralUnitId"
                reference={administrationRequests.getStructuralUnitsSelect()}
                link={false}
              >
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid item xs={12} md={4}>
            <Labeled label="От дата">
              <DoctoralDateField source="dateFrom" showTime />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={4}>
            <Labeled label="До дата">
              <DoctoralDateField source="dateTo" showTime />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={4}>
            <Labeled label="Конспект по език">
              <UrlField source="languageConspectLink" target="_blank" rel="noopener noreferrer" />
            </Labeled>
          </Grid>
        </Grid>

        <SectionTitle
          title="Докторантски места"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <SeatsSection />
        </Box>

        <SectionTitle
          title="Изпити"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
            По дисциплина
          </Typography>
          <ExamsSection />
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: "text.secondary" }}>
            По език
          </Typography>
          <LanguageExamsSection />
        </Box>

        <SectionTitle
          title="Документи за кандидатстване"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <DocumentTypesSection />
        </Box>

        <SecuredComponent permission={permissions.canPreviewDoctoralCampaignRankings}>
          <SectionTitle
            title="Класирания"
            textAlign="center"
            sx={{ marginTop: "2rem" }}
          />
          <Box
            sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
          >
            <RankingsSection />
          </Box>
        </SecuredComponent>

      </SimpleShowLayout>
    </Show>
  );
};

export default ShowDoctoralCampaign;
